import React, { useState , useEffect } from 'react';

import { clearProcessFeedback, continueProcess } from '../../../js/Helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faCopy } from '@fortawesome/free-solid-svg-icons';

import loading_gif from "../../../images/ajax-loader.gif";

import '../../../css/hsms/edit_modals.css';

export default function UploadConfirmDataValidation({ schoolInfo, progressID, selectedSchedules, feedbackData, fileHeaders, setShowUploadDataValidationFeedback, setUploadState, cancelUpload, fileUpload, handleCheckUpload, onValidationConfirmed, onComplete }) {
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [fileWarnings, setFileWarnings] = useState([]);
	const [excelStartIndex, setExcelStartIndex] = useState(0);
	const [copiedColumnIndex, setCopiedColumnIndex] = useState(null);

	const confirmDataValidation = async () => {
		// Set as submitting (to show loader in button)
		setIsSubmitting(true);

		// Create comma delimited list of schedule version ids
		const selected_schedules = selectedSchedules.join(',');

		const data = {school_id:schoolInfo.school_id, progress_id:progressID, data_version_id:schoolInfo.current_data_version_id, schedule_version_ids:selected_schedules, process_type:feedbackData.process_type, data:{headers:fileHeaders}};

		// Reset state, clear feedback, and keep upload going with the headers submitted
		setUploadState(null);
		if(onValidationConfirmed) onValidationConfirmed();
		
		await clearProcessFeedback(data);
		await continueProcess(data);
		handleCheckUpload(feedbackData.process_type, fileUpload, true, () => {
			onComplete();
		});
		
		setShowUploadDataValidationFeedback(false);
	}

	///////////////////////////////////////
	///// DO ON LOAD OF FEEDBACK DATA /////
	///////////////////////////////////////
	useEffect(()=> {
		if(feedbackData.data)
		{
			setFileWarnings([...feedbackData.data.warnings]);
			setIsLoading(false);
		}
	},[feedbackData]);

	const columnLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N','O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'AA', 'AB', 'AC', 'AD', 'AE', 'AF', 'AG', 'AH', 'AI', 'AJ', 'AK', 'AL', 'AM', 'AN','AO', 'AP', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AV', 'AW', 'AX', 'AY', 'AZ'];
	
	const max_columns_to_show = 5;
	const visibleHeaders = fileHeaders.slice(excelStartIndex, excelStartIndex + max_columns_to_show);
	const visibleColumnLetters = columnLetters.slice(excelStartIndex, excelStartIndex + max_columns_to_show);
	const visibleColumnCount = visibleHeaders.length;
	const canGoLeft = excelStartIndex > 0;
	const canGoRight = fileHeaders.length > (excelStartIndex + max_columns_to_show);
	const visibleEndIndex = visibleColumnCount > 0 ? (excelStartIndex + visibleColumnCount - 1) : excelStartIndex;
	const visibleStartLetter = visibleColumnLetters[0] || columnLetters[excelStartIndex] || 'A';
	const visibleEndLetter = columnLetters[visibleEndIndex] || visibleStartLetter;
	const finalColumnLetter = columnLetters[fileHeaders.length - 1] || visibleEndLetter;
	const visibleColumnRange = (visibleStartLetter === visibleEndLetter) ? visibleStartLetter : `${visibleStartLetter}-${visibleEndLetter}`;

	const getWarningMessage = (warningType) => {
		if(warningType === 'missing_course_code')
		{
			return 'The course code does not have a match in Edario.';
		}
		else if(warningType === 'blank_course_code')
		{
			return 'The course code is blank';
		}
		else if(warningType === 'missing_teacher_code')
		{
			return 'The teacher code does not have a match in Edario.';
		}
		else if(warningType === 'blank_teacher_code')
		{
			return 'The teacher code is blank';
		}
		else if(warningType === 'missing_classroom_code')
		{
			return 'The classroom does not have a match in Edario.';
		}
		else if(warningType === 'blank_classroom_code')
		{
			return 'The classroom is blank';
		}
		else if(warningType === 'missing_student_code')
		{
			return 'The student code does not have a match in Edario.';
		}
		else if(warningType === 'blank_student_code')
		{
			return 'The student code is blank';
		}

		return null;
	};

	const goToPreviousColumns = () => {
		if(!canGoLeft) return;
		setExcelStartIndex(Math.max(excelStartIndex - max_columns_to_show, 0));
	};

	const goToNextColumns = () => {
		if(!canGoRight) return;
		setExcelStartIndex(excelStartIndex + max_columns_to_show);
	};

	const getUniqueWarningValuesForColumn = (column_index) => {
		const unique_warning_values = [];
		const seen_warning_values = new Set();

		fileWarnings.forEach(warning => {
			if(!warning.warning_column || !warning.row_data || !warning.warning_column.some(warning_column_index => warning_column_index === column_index)) return;

			const raw_value = warning.row_data[column_index];
			const warning_value = (raw_value === null || raw_value === undefined) ? '' : `${raw_value}`.trim();
			if(!warning_value || seen_warning_values.has(warning_value)) return;

			seen_warning_values.add(warning_value);
			unique_warning_values.push(warning_value);
		});

		return unique_warning_values;
	};

	const copyTextToClipboard = async (text) => {
		if(navigator.clipboard && navigator.clipboard.writeText)
		{
			try {
				await navigator.clipboard.writeText(text);
				return;
			}
			catch(error) {
				// Fall back to the textarea copy path below.
			}
		}

		const textarea = document.createElement('textarea');
		textarea.value = text;
		textarea.setAttribute('readonly', '');
		textarea.style.position = 'fixed';
		textarea.style.left = '-9999px';
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand('copy');
		document.body.removeChild(textarea);
	};

	const copyWarningValuesForColumn = async (event, column_index) => {
		event.stopPropagation();

		const unique_warning_values = getUniqueWarningValuesForColumn(column_index);
		if(unique_warning_values.length === 0) return;

		await copyTextToClipboard(unique_warning_values.join('\n'));
		setCopiedColumnIndex(column_index);
		setTimeout(() => setCopiedColumnIndex(current_column_index => current_column_index === column_index ? null : current_column_index), 1500);
	};

	return (
		<div className='modal'>
			<div className='modal-content' style={{width:'930px',textAlign:'left', padding:'5px 35px 15px 35px'}}>
				<span className="modal-close-right" onClick={() => setShowUploadDataValidationFeedback(false)}>&times;</span>
				<h2>Data Validation</h2>
				{isLoading ?
				(
					<div className='center-text'>
						<img src={require('../../../images/balls.gif')} alt='Loading...' style={{height:'40px'}}/>
					</div>
				):
				(
					<>
					<h1 id='upload-confirm-data-validation-subheader'>We found {fileWarnings.length} potential data issue(s)...</h1>
					<div id='upload-data-validation-results'>
						<div className='upload-data-validation-sticky-topbar'>
							<div className='upload-data-validation-range-pill'>Showing columns {visibleColumnRange} of {finalColumnLetter}</div>
						</div>
						<div id='upload-data-validation-excel-sheet-parent-container'>
							<button type='button' className={`upload-data-validation-nav-pill upload-data-validation-nav-pill-left ${!canGoLeft ? 'disabled' : ''}`} onClick={goToPreviousColumns} aria-label='Show previous columns'>
								<FontAwesomeIcon icon={faChevronLeft}/>
							</button>
							<div id='upload-data-validation-excel-sheet'>
								<div className='upload-data-validation-excel-filler-row light-gray upload-data-validation-excel-filler-row-sticky-top'></div>
								{visibleColumnLetters.map((column_letter, j) => {
									return (
										<div className='upload-data-validation-excel-row-header upload-data-validation-excel-row-header-sticky' key={`letter-header-${excelStartIndex + j}`}>{column_letter}</div>
									)
								})}
								<div className='upload-data-validation-excel-filler-row light-gray upload-data-validation-excel-filler-row-sticky-second'></div>
								{visibleHeaders.map((header_info, j) => {
									const column_index = excelStartIndex + j;
									const unique_warning_values = getUniqueWarningValuesForColumn(column_index);
									const has_column_warnings = unique_warning_values.length > 0;

									return (
										<div className='upload-data-validation-excel-row-title upload-data-validation-excel-row-title-sticky' key={`header-name-${column_index}`}>
											<span className='ellipsis'>{header_info.display_name}</span>
											{has_column_warnings &&
												<button
													type='button'
													className={`upload-data-validation-copy-btn ${copiedColumnIndex === column_index ? 'copied' : ''}`}
													onClick={(event) => copyWarningValuesForColumn(event, column_index)}
													title={`Copy ${unique_warning_values.length} unique error value${unique_warning_values.length === 1 ? '' : 's'} from ${header_info.display_name}`}
													aria-label={`Copy unique error values from ${header_info.display_name}`}
												>
													<FontAwesomeIcon icon={faCopy}/>
												</button>
											}
										</div>
									)
								})}
								{(visibleColumnCount < max_columns_to_show) &&
									<>
									{[...Array(max_columns_to_show - visibleColumnCount).keys()].map(index => {
										return (
											<div className='upload-data-validation-excel-row-title upload-data-validation-excel-row-title-sticky' key={`filler-header-name-${index}}`}></div>
										)
									})}
									</>
								}
									{fileWarnings.map(warning => {
										const warning_type = warning.warning_type;
										const warning_column = warning.warning_column;
										const row_data = warning.row_data;
										const row_number = warning.row_number;
										const visibleRowData = row_data.slice(excelStartIndex, excelStartIndex + max_columns_to_show);

									return (
										<React.Fragment key={`warning-row-${row_number}`}>
										<div className='upload-data-validation-excel-filler-row light-gray'>{row_number}</div>
										{visibleRowData.map((column_data, visible_index) => {
											const k = excelStartIndex + visible_index;

											const warning_column_array_index = warning_column.findIndex(column_index => column_index === k);
											const current_warning_type = (warning_column_array_index !== -1) ? warning_type[warning_column_array_index] : null;
											const warning_message = getWarningMessage(current_warning_type);

											return (
												<div className={`upload-data-validation-excel-row ${warning_message ? 'excel-cell-error' : ''}`} key={`warning-column-${row_number}-${k}`} title={warning_message ? warning_message : (column_data ? column_data : '')}><span className='ellipsis'>{column_data}</span></div>
											)
										})}
										{(visibleRowData.length < max_columns_to_show) &&
											<>
											{[...Array(max_columns_to_show - visibleRowData.length).keys()].map(index => {
												return (
													<div className='upload-data-validation-excel-row' key={`filler-warning-row-${row_number}-${index}`}></div>
												)
											})}
											</>
										}
										</React.Fragment>
									)
								})}
							</div>
							<button type='button' className={`upload-data-validation-nav-pill upload-data-validation-nav-pill-right ${!canGoRight ? 'disabled' : ''}`} onClick={goToNextColumns} aria-label='Show next columns'>
								<FontAwesomeIcon icon={faChevronRight}/>
							</button>
						</div>
					</div>
					<div className='upload-confirm-data-validation-subheader-question'>Would you still like to continue the upload?</div>
					<div className='edit-upload-headers-btn-container'>
						<div className='btn gray-btn' onClick={cancelUpload}>Cancel Upload</div>
						<div className='btn green-btn' onClick={confirmDataValidation}>
							{isSubmitting ? (
								<><img src={loading_gif} alt='loading...' style={{height:'20px'}}/></>
							): (
								<>Continue Upload</>
							)}
						</div>
					</div>
					</>
				)
				}
			</div>
		</div>
	);
}
